/**
 * Notifications Page — full list with filters (All / Unread / By type)
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Bell, Check, Trash2, Inbox, ArrowLeft } from "lucide-react";
import { useAuth, useLanguage, API } from "@/context/AppContext";

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications/history?limit=100`, { withCredentials: true });
      setItems(res.data.notifications || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetch();
  }, [user?.user_id, user?.active_role]);

  const markAllRead = async () => {
    await axios.post(`${API}/notifications/read-all`, {}, { withCredentials: true });
    await fetch();
    toast.success("All marked as read");
  };

  const removeOne = async (n) => {
    const id = n.notification_id || n.id || n._id_str;
    if (!id) return;
    await axios.delete(`${API}/notifications/${id}`, { withCredentials: true });
    await fetch();
  };

  const filtered = items.filter((n) => {
    if (filter === "all") return true;
    if (filter === "unread") return !n.read;
    return (n.template_type || n.type) === filter;
  });

  // Build dynamic list of available types from the current items
  const types = Array.from(new Set(items.map((n) => n.template_type || n.type).filter(Boolean)));

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8" data-testid="notifications-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="notifications-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold">Notifications</h1>
          </div>
          <Button size="sm" variant="outline" onClick={markAllRead} data-testid="notifications-mark-all-read">
            <Check className="w-4 h-4 mr-1" />
            Mark all as read
          </Button>
        </div>

        <Card className="mb-4">
          <CardContent className="p-3">
            <Tabs value={filter} onValueChange={setFilter}>
              <TabsList data-testid="notifications-filter-tabs">
                <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
                <TabsTrigger value="unread" data-testid="filter-unread">Unread</TabsTrigger>
                {types.slice(0, 4).map((t) => (
                  <TabsTrigger key={t} value={t} data-testid={`filter-type-${t}`}>{t}</TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center text-gray-400">{t("loading") || "Loading…"}</div>
            ) : filtered.length === 0 ? (
              <div className="p-16 text-center text-gray-400" data-testid="notifications-empty">
                <Inbox className="w-10 h-10 mx-auto mb-3 opacity-40" />
                No notifications
              </div>
            ) : (
              <div className="divide-y">
                {filtered.map((n, idx) => (
                  <div
                    key={n.id || n._id_str || idx}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors flex gap-3 ${
                      !n.read ? "bg-blue-50/40" : ""
                    }`}
                    onClick={() => n.link && navigate(n.link)}
                    data-testid={`notification-row-${idx}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-blue-100 text-[#0044CC] flex items-center justify-center flex-shrink-0">
                      <Bell className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{n.title || "Notification"}</span>
                        {!n.read && <Badge variant="secondary" className="text-[10px]">New</Badge>}
                      </div>
                      <p className="text-sm text-gray-600 mt-0.5">{n.body || ""}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {n.sent_at ? new Date(n.sent_at).toLocaleString() : ""}
                      </p>
                    </div>
                    <button
                      className="text-gray-300 hover:text-red-500"
                      onClick={(e) => { e.stopPropagation(); removeOne(n); }}
                      data-testid={`notification-delete-${idx}`}
                      aria-label="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NotificationsPage;
